import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaBookOpen, 
  FaHeadphones, 
  FaDownload, 
  FaUsers, 
  FaStar, 
  FaPlay,
  FaLanguage,
  FaHeart,
  FaBars,
  FaFacebook,
  FaTwitter,
  FaInstagram,
  FaLinkedin
} from 'react-icons/fa';
import './App.css';

function App() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    {
      icon: <FaHeadphones />,
      title: 'Premium Audio Stories',
      description: 'Immerse yourself in high-quality narrated stories from talented creators worldwide.'
    },
    {
      icon: <FaLanguage />,
      title: 'Multi-Language Support',
      description: 'Enjoy stories in multiple languages with seamless language switching capabilities.'
    },
    {
      icon: <FaDownload />,
      title: 'Offline Listening',
      description: 'Download your favorite stories and listen anywhere, anytime, without internet.'
    },
    {
      icon: <FaUsers />,
      title: 'Creator Community',
      description: 'Join a vibrant community of storytellers and share your own audio stories.'
    },
    {
      icon: <FaPlay />,
      title: 'Smart Playback',
      description: 'Advanced playback controls with speed adjustment, bookmarks, and sleep timer.'
    },
    {
      icon: <FaHeart />,
      title: 'Personalized Library',
      description: 'Create your own library with favorites, playlists, and listening history.'
    }
  ];

  return (
    <div className="App">
      {/* Navigation */}
      <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="nav-container">
          <a href="#home" className="nav-logo">
            <FaBookOpen className="nav-logo-icon" />
            <span>Hadithi</span>
          </a>
          
          <ul className="nav-links">
            <li><a href="#features">Features</a></li>
            <li><a href="#about">About</a></li>
            <li><a href="#download">Download</a></li>
            <li><a href="#contact">Contact</a></li>
          </ul>
          
          <a href="#download" className="nav-cta">Get the App</a>
          
          <button className="mobile-menu-button">
            <FaBars />
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="hero">
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-badge">
              <FaStar />
              <span>Top Rated Story App 2026</span>
            </div>
            
            <h1 className="hero-title">
              Discover Stories <br />
              That <span className="highlight">Inspire</span>
            </h1>
            
            <p className="hero-description">
              Experience the magic of storytelling with Hadithi. Listen to captivating audio 
              stories anytime, anywhere. Join thousands of listeners and creators today.
            </p>
            
            <div className="hero-buttons">
              <a href="https://github.com/Raxan7/hadithi-website/releases/download/v1.0.0/hadithi-app.apk" className="btn-primary" target="_blank" rel="noopener noreferrer">
                <FaDownload />
                <span>Download for App (Android)</span>
              </a>
            </div>
          </div>
          
          <div className="hero-image">
            <div className="phone-mockup">
              <div className="phone-screen">
                <div className="app-icon">
                  <FaBookOpen />
                </div>
                <div className="app-name">Hadithi</div>
                <div className="app-tagline">Stories That Inspire</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features">
        <div className="features-container">
          <div className="section-header">
            <div className="section-badge">Features</div>
            <h2 className="section-title">Everything You Need</h2>
            <p className="section-description">
              Hadithi brings you a complete storytelling experience with powerful 
              features designed for both listeners and creators.
            </p>
          </div>
          
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon">{feature.icon}</div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Screenshots Section */}
      <section id="about" className="screenshots">
        <div className="screenshots-container">
          <div className="section-header">
            <div className="section-badge">Experience</div>
            <h2 className="section-title">Beautiful Design</h2>
            <p className="section-description">
              A modern, intuitive interface that makes discovering and listening 
              to stories a delightful experience.
            </p>
          </div>
          
          <div className="screenshots-grid">
            {[1, 2, 3, 4].map((item) => (
              <div key={item} className="screenshot-card">
                <div className="screenshot-image">
                  <FaBookOpen />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="download" className="cta">
        <div className="cta-container">
          <h2 className="cta-title">Ready to Start Listening?</h2>
          <p className="cta-description">
            Hadithi is currently available for Android phones. Join thousands of users 
            who are already enjoying captivating stories. Get the app and begin your journey today.
          </p>
          
          <div className="cta-buttons">
            <a href="https://github.com/Raxan7/hadithi-website/releases/download/v1.0.0/hadithi-app.apk" className="btn-primary btn-white" target="_blank" rel="noopener noreferrer">
              <FaDownload />
              <span>Download for App (Android)</span>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="footer">
        <div className="footer-container">
          <div className="footer-about">
            <div className="footer-logo">
              <FaBookOpen />
              <span>Hadithi</span>
            </div>
            <p className="footer-description">
              Bringing stories to life through the power of audio. 
              Connect with storytellers and listeners from around the world.
            </p>
            <div className="social-links">
              <a href="https://facebook.com" className="social-link" rel="noopener noreferrer" target="_blank"><FaFacebook /></a>
              <a href="https://twitter.com" className="social-link" rel="noopener noreferrer" target="_blank"><FaTwitter /></a>
              <a href="https://instagram.com" className="social-link" rel="noopener noreferrer" target="_blank"><FaInstagram /></a>
              <a href="https://linkedin.com" className="social-link" rel="noopener noreferrer" target="_blank"><FaLinkedin /></a>
            </div>
          </div>
          
          <div className="footer-section">
            <h3>Product</h3>
            <ul className="footer-links">
              <li><Link to="/#features">Features</Link></li>
              <li><Link to="/#download">Download</Link></li>
              <li><Link to="/pricing">Pricing</Link></li>
              <li><Link to="/updates">Updates</Link></li>
            </ul>
          </div>
          
          <div className="footer-section">
            <h3>Company</h3>
            <ul className="footer-links">
              <li><Link to="/#about">About Us</Link></li>
              <li><Link to="/careers">Careers</Link></li>
              <li><Link to="/blog">Blog</Link></li>
              <li><Link to="/#contact">Contact</Link></li>
            </ul>
          </div>
          
          <div className="footer-section">
            <h3>Legal</h3>
            <ul className="footer-links">
              <li><Link to="/privacy">Privacy Policy</Link></li>
              <li><Link to="/terms">Terms of Service</Link></li>
              <li><Link to="/cookies">Cookie Policy</Link></li>
              <li><Link to="/licenses">Licenses</Link></li>
            </ul>
          </div>
        </div>
        
        <div className="footer-bottom">
          <p>&copy; 2026 Hadithi. All rights reserved. Made with ❤️ for storytellers.</p>
        </div>
      </footer>
    </div>
  );
}

export default App;
